import { formatDateTime } from '@/lib/date-time';
/**
 * FailedOutboxList.tsx
 *
 * 同步失敗項清單組件。
 *
 * 背景：outbox 條目重試 5 次耗盡、或遇 HTTP 409 衝突後會被標為 status='failed'，
 * 不再自動重試。此前這些條目在 UI 完全不可見，現場填寫的資料會靜默滯留本機。
 * 本組件把 failed 條目列出來，讓用戶逐筆決定：
 * - 重試：重新入隊（pending），下一輪同步再送一次
 * - 以本機覆蓋：僅對 body 含 version 的條目（表單版本衝突）——移除 version 後重送，
 *   後端跳過版本比對，等同用本機離線版本覆蓋線上版本
 * - 放棄：刪除條目，本機修改不再上傳
 *
 * 掛載位置：離線管理頁（/me/storage）的「同步失敗項」卡片。
 */
import { useCallback, useEffect, useState } from 'react';
import { Button, Empty, List, Space, Tag, Typography } from 'antd';
import { RefreshCw, Trash2, UploadCloud } from 'lucide-react';
import { message, modal } from '@/lib/antd-app';
import { deleteOutbox, listFailedOutbox, requeueFailedOutbox, type OutboxEntry } from '@/pwa/db';
import { syncOnce } from '@/pwa/sync';

const { Text } = Typography;

/**
 * 把 outbox 條目轉成用戶可讀的操作描述。
 * 依 url + method 匹配常見離線操作；未知路徑回落顯示原始 method + url。
 */
function describeEntry(entry: OutboxEntry): string {
  const { method, url } = entry;
  if (url.includes('/check-in')) return '現場報到打卡';
  if (url.includes('/check-out')) return '現場離場打卡';
  if (/\/form-instances\/\d+/.test(url)) return '表單填寫內容';
  if (url.includes('/findings')) {
    return method === 'POST' ? '新增不符合項（NC）' : '更新不符合項（NC）';
  }
  if (url.includes('/samples')) {
    return method === 'POST' ? '新增採樣記錄' : '更新採樣記錄';
  }
  return `${method} ${url}`;
}

/** 條目 body 是否帶樂觀鎖 version（可提供「以本機覆蓋」選項） */
function hasBodyVersion(entry: OutboxEntry): boolean {
  return (
    !!entry.body &&
    typeof entry.body === 'object' &&
    !Array.isArray(entry.body) &&
    'version' in (entry.body as Record<string, unknown>)
  );
}

/** 失敗原因是否為 409 衝突（lastError 由 sync.ts 以 'Conflict: ' 前綴寫入） */
function isConflict(entry: OutboxEntry): boolean {
  return (entry.lastError ?? '').startsWith('Conflict:');
}

export interface FailedOutboxListProps {
  /** 條目被重試/放棄後回調（父層刷新 pending/failed 計數） */
  onChanged?: () => void;
}

/**
 * 同步失敗項清單。無 failed 條目時顯示空狀態。
 */
export function FailedOutboxList({ onChanged }: FailedOutboxListProps) {
  const [entries, setEntries] = useState<OutboxEntry[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [retryingAll, setRetryingAll] = useState(false);

  /** 重新讀取 failed 條目 */
  const reload = useCallback(async () => {
    try {
      setEntries(await listFailedOutbox());
    } catch (err) {
      // IndexedDB 讀取失敗屬異常環境（隱私模式等），提示但不崩頁
      const msg = err instanceof Error ? err.message : String(err);
      message.error(`讀取失敗項清單失敗：${msg}`);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** 重新入隊後：在線則立即觸發一次同步，並刷新清單與父層計數 */
  const afterRequeue = useCallback(async () => {
    if (navigator.onLine) {
      try {
        await syncOnce();
      } catch (err) {
        console.warn('[failed-outbox] syncOnce after requeue failed', err);
      }
    }
    await reload();
    onChanged?.();
  }, [reload, onChanged]);

  /** 單筆重試（可選強制覆蓋：移除 body.version） */
  async function retryOne(entry: OutboxEntry, dropBodyVersion: boolean) {
    setBusyId(entry.id);
    try {
      const ok = await requeueFailedOutbox(entry.id, { dropBodyVersion });
      if (!ok) {
        message.warning('條目狀態已變更，請重新整理');
        await reload();
        return;
      }
      message.success(dropBodyVersion ? '已改為以本機版本覆蓋並重新排隊' : '已重新排隊等待同步');
      await afterRequeue();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      message.error(`重試失敗：${msg}`);
    } finally {
      setBusyId(null);
    }
  }

  /** 以本機覆蓋線上（版本衝突專用），需二次確認 */
  function confirmForceOverwrite(entry: OutboxEntry) {
    modal.confirm({
      title: '以本機版本覆蓋線上？',
      content:
        '此條目與線上版本發生衝突（線上已被他人更新）。覆蓋後，線上的修改將被本機離線填寫的內容取代，且無法復原。',
      okText: '確定覆蓋',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => retryOne(entry, true),
    });
  }

  /** 放棄本機修改（刪除條目），需二次確認 */
  function confirmDiscard(entry: OutboxEntry) {
    modal.confirm({
      title: '放棄這筆本機修改？',
      content: `「${describeEntry(entry)}」將從本機佇列刪除，離線填寫的這筆內容不會上傳，且無法復原。`,
      okText: '確定放棄',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteOutbox(entry.id);
          message.success('已放棄該筆本機修改');
          await reload();
          onChanged?.();
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          message.error(`刪除失敗：${msg}`);
        }
      },
    });
  }

  /** 全部重試（不含強制覆蓋；衝突條目重試後大概率再次衝突，需逐筆決定） */
  async function retryAll() {
    setRetryingAll(true);
    try {
      for (const entry of entries) {
        await requeueFailedOutbox(entry.id);
      }
      message.success(`已重新排隊 ${entries.length} 筆`);
      await afterRequeue();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      message.error(`批次重試失敗：${msg}`);
    } finally {
      setRetryingAll(false);
    }
  }

  if (entries.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="沒有同步失敗的項目" />;
  }

  return (
    <div>
      <div className="mb-2">
        <Button
          size="small"
          icon={<RefreshCw size={14} />}
          loading={retryingAll}
          onClick={retryAll}
        >
          全部重試
        </Button>
      </div>
      <List
        size="small"
        dataSource={entries}
        rowKey={(e) => e.id}
        renderItem={(entry) => (
          <List.Item
            actions={[
              <Button
                key="retry"
                size="small"
                icon={<RefreshCw size={14} />}
                loading={busyId === entry.id}
                onClick={() => void retryOne(entry, false)}
              >
                重試
              </Button>,
              // 僅版本衝突條目提供「以本機覆蓋」：body 帶 version 才有覆蓋語義
              ...(isConflict(entry) && hasBodyVersion(entry)
                ? [
                    <Button
                      key="force"
                      size="small"
                      danger
                      icon={<UploadCloud size={14} />}
                      onClick={() => confirmForceOverwrite(entry)}
                    >
                      以本機覆蓋
                    </Button>,
                  ]
                : []),
              <Button
                key="discard"
                size="small"
                danger
                type="text"
                icon={<Trash2 size={14} />}
                onClick={() => confirmDiscard(entry)}
              >
                放棄
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={
                <Space>
                  <span>{describeEntry(entry)}</span>
                  {isConflict(entry) ? (
                    <Tag color="orange">版本衝突</Tag>
                  ) : (
                    <Tag color="red">重試耗盡</Tag>
                  )}
                </Space>
              }
              description={
                <Space direction="vertical" size={0}>
                  <Text type="secondary">
                    建立於 {formatDateTime(entry.createdAt)}
                    {entry.taskId ? ` ・任務 #${entry.taskId}` : ''}
                    {entry.caseId ? ` ・案件 #${entry.caseId}` : ''}
                  </Text>
                  {entry.lastError && <Text type="danger">{entry.lastError}</Text>}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
}
